import { DateTime as d } from "luxon";

import {
  BILLING_CYCLE_NAME,
  IAddonRequirementInput,
  IBookingCreateInput,
  IBookingProtectionPlanInput,
  IBookingVehicleGroup,
  ILocationTaxSurcharge,
  IOneWayRentalFee,
  ITaxInput,
  PriceRuleOperator,
  ValueType
} from "../../reducers/bookings/types";
import { IBranch } from "../../reducers/website/types";
import { getRetnalTaxAmount, getVehicleGroupApplicableTaxesDetails, isLongTermBooking } from "../utils";
import { RATE_TYPES } from "../views/utils";

export function getApplicableRentalAmount(
  baseRate: number,
  operator?: string,
  valueType?: ValueType,
  value?: number,
  riskBasedPricingApplied?: boolean,
  riskBasedPricingAmount?: number
) {
  let applicableRentalAmount = baseRate;
  if (operator && valueType && value) {
    switch (operator) {
      case PriceRuleOperator.SURGE:
        if (valueType === ValueType.VARIABLE) {
          applicableRentalAmount = baseRate + Math.round((baseRate * value) / 100);
        } else {
          applicableRentalAmount = baseRate + value;
        }
        break;
      case PriceRuleOperator.DISCOUNT:
        if (valueType === ValueType.VARIABLE) {
          applicableRentalAmount = baseRate - Math.round((baseRate * value) / 100);
        } else {
          applicableRentalAmount = baseRate - value;
        }
        applicableRentalAmount = Math.max(applicableRentalAmount, 0);
        break;
    }
  }
  if (riskBasedPricingApplied) {
    applicableRentalAmount += riskBasedPricingAmount ?? 0;
  }
  return applicableRentalAmount;
}

export function getAddonRate(addon: IAddonRequirementInput, rentalTime: number) {
  let rate = addon.rate * addon.quantity;
  if (!addon.hasFixedRate && rentalTime) {
    rate = rate * rentalTime;
  }
  return rate;
}

export function getProtectionPlanRate(protectionPlan: IBookingProtectionPlanInput, rentalTime: number) {
  let rate = protectionPlan.rate;
  if (!protectionPlan.hasFixedRate && rentalTime) {
    rate = rate * rentalTime;
  }
  return rate;
}

export function deriveBranchTaxes(branch?: IBranch): ITaxInput[] {
  if (!branch || !branch.addTaxOptIn) {
    return [];
  }
  return branch.taxes.map((tax) => ({
    code: tax.title,
    type: tax.type,
    rate: tax.rate,
    amount: tax.value,
    isVehicleGroupSpecific: tax.isVehicleGroupSpecific || false,
    vehicleGroups: tax.vehicleGroups || [],
    subdivisionOptIn: tax.subdivisionOptIn,
    subdivisionRate: tax.subdivisionRate,
    subdivisionType: tax.subdivisionType,
    subdivisionValue: tax.subdivisionValue
  }));
}

export interface IPriceBreakdown {
  rentalPrice: number;
  insurancePrice: number;
  addonsPrice: number;
  totalPrice: number;
}

/**
 * Mirrors SummaryPanel/index.tsx's calculateBookingDurationInDays useEffect: for long-term
 * (recurring/weekly/monthly) bookings this returns a billing-frequency-derived day count used
 * only for tax calculation; for standard bookings it's the plain pickup-to-dropoff day span.
 */
function calculateBookingDurationInDays(bookingInput: IBookingCreateInput): number {
  if (!bookingInput.pickupDateTime) {
    return 0;
  }
  if (isLongTermBooking(bookingInput)) {
    switch (bookingInput.rateTypeName) {
      case BILLING_CYCLE_NAME.DAILY:
        return bookingInput.isRecurringBilling && bookingInput.billingFrequency ? bookingInput.billingFrequency : 0;
      case BILLING_CYCLE_NAME.WEEKLY:
        return bookingInput.isRecurringBilling && bookingInput.billingFrequency ? 7 * bookingInput.billingFrequency : 7;
      case BILLING_CYCLE_NAME.MONTHLY: {
        if (bookingInput.isRecurringBilling && bookingInput.billingFrequency) {
          const idealEndDate = d.fromISO(bookingInput.pickupDateTime).plus({ months: bookingInput.billingFrequency }).toUTC().toISO();
          return d.fromISO(idealEndDate).diff(d.fromISO(bookingInput.pickupDateTime), "day").days;
        }
        return 0;
      }
      default:
        return 0;
    }
  }
  return bookingInput.dropoffDateTime
    ? Math.ceil(d.fromISO(bookingInput.dropoffDateTime).diff(d.fromISO(bookingInput.pickupDateTime), ["days"]).days)
    : 0;
}

/**
 * Mirrors the pricing math in SummaryPanel/index.tsx (both the standard-rental totalAmount
 * accumulation and the subscription-specific totalSubscriptionAmount accumulation), so a
 * customer's booking enquiry can report the same rental/add-ons/total split they were actually
 * shown, for either kind of booking. Keep this in sync if SummaryPanel's calculation changes.
 */
export function calculatePriceBreakdown(
  bookingInput: IBookingCreateInput,
  taxes: ITaxInput[],
  taxExempted: boolean,
  oneWayRentalFee?: IOneWayRentalFee
): IPriceBreakdown {
  const isSubscription = Boolean(bookingInput?.isSubscription);
  if (!bookingInput?.pickupDateTime || (!isSubscription && (!bookingInput.vehicleGroups?.length || !bookingInput.dropoffDateTime))) {
    return { rentalPrice: 0, insurancePrice: 0, addonsPrice: 0, totalPrice: 0 };
  }

  const rentalTime = bookingInput.dropoffDateTime
    ? ([RATE_TYPES.WEEKLY, RATE_TYPES.MONTHLY].includes(bookingInput.rateTypeName as RATE_TYPES) ? 1
      : bookingInput.rateTypeName === RATE_TYPES.DAILY
        ? Math.ceil(d.fromISO(bookingInput.dropoffDateTime).diff(d.fromISO(bookingInput.pickupDateTime), "days").days)
        : Math.ceil(d.fromISO(bookingInput.dropoffDateTime).diff(d.fromISO(bookingInput.pickupDateTime), "hours").hours))
    : 0;

  const bookingDurationInDays = calculateBookingDurationInDays(bookingInput);

  let totalVehicles = bookingInput.vehicleGroups?.reduce((sum, vg) => sum + vg.count, 0) || 0;
  if (isSubscription) {
    totalVehicles = 1;
  }

  let rentalPrice = 0;
  let insurancePrice = 0;
  let addonsPrice = 0;
  let totalRentalAmount = 0;

  if (isSubscription) {
    // Mirrors SummaryPanel: subscription cost comes from activeSubscriptionExpanded, not the
    // vehicleGroups rental loop (which totalSubscriptionAmount never accumulates from).
    // activeSubscriptionExpanded is only populated when re-loading an already-created booking
    // for editing - a freshly selected (not yet created) subscription only has `activeSubscription`
    // (singular) set, so fall back to that for the pre-booking case.
    const subscriptionAmount = bookingInput.activeSubscriptionExpanded?.subscriptionAmount
      ?? bookingInput.activeSubscription?.subscriptionAmount ?? 0;
    const enrollmentAmount = bookingInput.activeSubscriptionExpanded?.enrollmentAmount
      ?? bookingInput.activeSubscription?.enrollmentAmount ?? 0;
    const subscriptionTaxAmount = getRetnalTaxAmount(taxExempted ? [] : taxes, subscriptionAmount, bookingDurationInDays, 1);
    rentalPrice = subscriptionAmount + enrollmentAmount + subscriptionTaxAmount;
  } else {
    bookingInput.vehicleGroups.forEach((vg: IBookingVehicleGroup) => {
      const rentalAmount = getApplicableRentalAmount(
        vg.baseRate, vg.priceRuleOperator, vg.priceRuleValueType, vg.priceRuleAmount, vg.riskBasedPricingApplied, vg.riskBasedPricingAmount
      );
      const vgTotalRentalAmount = rentalAmount * vg.count * rentalTime;
      totalRentalAmount += vgTotalRentalAmount;
      const applicableTaxes = getVehicleGroupApplicableTaxesDetails(
        taxes as any, typeof vg.vehicleGroup === "string" ? vg.vehicleGroup : vg.vehicleGroup.id
      );
      const rentalTaxAmount = getRetnalTaxAmount(taxExempted ? [] : applicableTaxes, vgTotalRentalAmount, bookingDurationInDays, vg.count);
      rentalPrice += vgTotalRentalAmount + rentalTaxAmount;
    });
  }

  // Insurance - mirrors SummaryPanel: a percentage-based rate REPLACES the flat-rate
  // contribution rather than adding to it, and reduces to the last vehicle group's
  // applicable rate when there are multiple groups. Preserved here for parity.
  let insuranceAmount = (bookingInput.insuranceRate || 0) * totalVehicles * rentalTime;
  if (bookingInput.insurancePercentageRate) {
    let rentalUnitPrice = 0;
    bookingInput.vehicleGroups.forEach((vg: IBookingVehicleGroup) => {
      rentalUnitPrice = getApplicableRentalAmount(
        vg.baseRate, vg.priceRuleOperator, vg.priceRuleValueType, vg.priceRuleAmount, vg.riskBasedPricingApplied, vg.riskBasedPricingAmount
      );
    });
    insuranceAmount = (bookingInput.insurancePercentageRate * rentalUnitPrice) / 100 * rentalTime;
  }
  insurancePrice += insuranceAmount;

  // Protection plan
  if (bookingInput.protectionPlan) {
    const planTaxAmount = taxExempted ? 0 : (
      bookingInput.protectionPlan.taxable && bookingInput.protectionPlan.tax?.value
        ? Math.round(bookingInput.protectionPlan.rate * bookingInput.protectionPlan.tax.value / 100) * (bookingInput.protectionPlan.hasFixedRate ? 1 : rentalTime)
        : 0
    );
    addonsPrice += getProtectionPlanRate(bookingInput.protectionPlan, rentalTime) + planTaxAmount;
  }

  // Add-ons
  bookingInput.addonRequirements?.forEach((addon) => {
    let taxAmount = 0;
    if (!taxExempted && addon.tax?.value) {
      taxAmount = addon.hasFixedRate
        ? Math.round(addon.rate * addon.tax.value / 100) * addon.quantity
        : Math.round(addon.rate * addon.tax.value / 100) * addon.quantity * rentalTime;
    }
    addonsPrice += getAddonRate(addon, rentalTime) + taxAmount;
  });

  // Location surcharges
  bookingInput.locationSurcharges?.forEach((surcharge: ILocationTaxSurcharge) => {
    let surchargeAmount = 0;
    let surchargeTaxAmount = 0;
    if (surcharge.type === "PERCENTAGE") {
      surchargeAmount = Math.round(totalRentalAmount * surcharge.value / 100);
      surchargeTaxAmount = taxExempted ? 0 : Math.round(surchargeAmount * surcharge.tax.value / 100);
    } else {
      if (!taxExempted && surcharge.tax?.value) {
        if (surcharge.rate === "PER_DAY") {
          surchargeTaxAmount = (surcharge.value * surcharge.tax.value / 100) * totalVehicles * rentalTime;
        } else if (surcharge.rate === "ONE_TIME") {
          surchargeTaxAmount = (surcharge.value * surcharge.tax.value / 100) * totalVehicles;
        }
      }
      if (surcharge.rate === "PER_DAY") {
        surchargeAmount = surcharge.value * totalVehicles * rentalTime;
      } else if (surcharge.rate === "ONE_TIME") {
        surchargeAmount = surcharge.value * totalVehicles;
      }
    }
    addonsPrice += surchargeAmount + surchargeTaxAmount;
  });

  // One-way rental fee - rental-only concept, mirrors SummaryPanel (only added to
  // totalAmount, never to totalSubscriptionAmount)
  if (!isSubscription && oneWayRentalFee?.amount) {
    addonsPrice += oneWayRentalFee.amount * totalVehicles;
  }

  return {
    rentalPrice,
    insurancePrice,
    addonsPrice,
    totalPrice: rentalPrice + insurancePrice + addonsPrice
  };
}
